// The Company's fulfillment standing + the finished goods it already fulfils,
// served by Nexus for the fulfillment section's SKU question (ADR-015 phase 6).
//
//   POST /api/erp/companies/fulfillment-status  →  FulfillmentStatus
//
// Two questions in one read: "does Argo have a fulfillment agreement with this
// company?" and "which SKUs do they already fulfil?", so the section can offer
// "put it into one of these" beside "create a new one".
//
// THE AGREEMENT IS A BOOLEAN HERE, DELIBERATELY. Nexus's response also carries
// `agreement` / `pending_agreement` objects (opportunity ids, quote numbers,
// dates) and a `fulfillment_service_names` list. The portal does NOT ingest
// them: either Argo has a recurring-billing agreement with this company or it
// does not, and that single fact is the entire branch. The wire being richer
// than the need is not a reason to consume the difference.
//
// Two outcomes follow from the boolean, and neither of them prices anything:
//   true  → which SKU does this assembly/kit outcome go into? (pick one, or
//           create a new one) — "put it into our stock under the agreement".
//   false → defer to sales to set an agreement up.
// Nexus flips the boolean on a WON deal only, so a service order still being set
// up reads as false — which is right, that IS the defer-to-sales case.
//
// Once fulfillment/recurring billing is in play this is no longer a print quote,
// and the portal quotes no part of it. Same posture as every other order
// service: transmitted intent, never priced, never folded into
// total_sale_amount.
//
// `sku` IS the selection key — there is no opaque id here, deliberately unlike
// CompanyItem's item_id, because Nexus never emits Material.id on this feed. A row
// without a sku cannot be picked and is dropped.
//
// Sanitize-at-boundary, same posture as normalizeCompanyItem: every row is
// narrowed to the allowlist below, so anything else Nexus might one day return —
// least of all a cost column — cannot propagate into the portal.
//
// NOT FulfillmentCoverageDto (types/quote.ts). That one is per-QUOTE and
// post-approval: it rides QuoteStatusDto and answers "does an existing service
// order already cover THIS order's recurring lines, so we skip a second
// signature?" This one is per-COMPANY and pre-quote: it answers "does this
// company have an agreement at all, and what do they already fulfil?" to drive
// the configurator's SKU picker. Different stage, scope, endpoint, and consumer
// — related questions, deliberately not one type.
import { normalizeBomComponent } from '../vocabulary.js';
// What every degrade path resolves to: no agreement, no SKUs — i.e. defer to
// sales. Matches what Nexus itself returns for an unknown company, so the
// portal's degraded state and Nexus's empty state render identically.
//
// ⚠️ A Nexus outage also lands here, and is indistinguishable from a genuine
// "no agreement" — acceptable because both route the customer to sales, but it
// means the false-branch copy must say what happens next and must never assert
// "you have no agreement with us" as a fact about Argo's records.
// Frozen — object and array both — because it is a module-level singleton: an
// accidental `EMPTY_FULFILLMENT_STATUS.skus.push(...)` would otherwise corrupt
// every later degrade in the process. Callers that need a standing they can hand
// onward build one with emptyFulfillmentStatus() instead of spreading this.
export const EMPTY_FULFILLMENT_STATUS = Object.freeze({
    has_fulfillment_agreement: false,
    skus: [],
});
Object.freeze(EMPTY_FULFILLMENT_STATUS.skus);
// A fresh empty standing, safe to mutate and return.
//
// Spreading EMPTY_FULFILLMENT_STATUS is NOT equivalent: `{ ...x }` is shallow, so
// every copy would share the one `skus` array and a single caller mutating its
// result would poison the constant for the whole process.
export function emptyFulfillmentStatus() {
    return { has_fulfillment_agreement: false, skus: [] };
}
function optionalString(value) {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
// Returns null (dropped) when sku is absent/blank — without it the row cannot be
// selected, which is the only thing this catalog exists to allow.
export function normalizeFulfilledSku(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const r = raw;
    if (typeof r.sku !== 'string' || r.sku.length === 0)
        return null;
    const components = Array.isArray(r.components)
        ? r.components.map(normalizeBomComponent).filter((c) => c !== null)
        : [];
    const out = {
        sku: r.sku,
        name: typeof r.name === 'string' && r.name.length > 0 ? r.name : r.sku,
        kind: r.kind === 'kit' ? 'kit' : 'finished_good',
        status: r.status === 'inactive' ? 'inactive' : 'active',
        component_count: typeof r.component_count === 'number' && Number.isFinite(r.component_count)
            ? r.component_count
            : components.length,
        components,
    };
    const lastBuiltAt = optionalString(r.last_built_at);
    if (lastBuiltAt !== undefined)
        out.last_built_at = lastBuiltAt;
    const updatedAt = optionalString(r.updated_at);
    if (updatedAt !== undefined)
        out.updated_at = updatedAt;
    return out;
}
// Narrow a whole untrusted response down to the two things the portal acts on.
// Never throws and never returns null — an unrecognizable body degrades to the
// empty standing, matching how the section behaves on a Nexus outage.
//
// This is where Nexus's richer payload is deliberately dropped: `agreement`,
// `pending_agreement` and `fulfillment_service_names` are read by nothing here,
// so they never enter the portal at all. Anything the boolean cannot express is
// a question for sales, not a branch in this form.
export function normalizeFulfillmentStatus(raw) {
    if (typeof raw !== 'object' || raw === null)
        return emptyFulfillmentStatus();
    const r = raw;
    return {
        has_fulfillment_agreement: r.has_fulfillment_agreement === true,
        skus: Array.isArray(r.skus)
            ? r.skus.map(normalizeFulfilledSku).filter((s) => s !== null)
            : [],
    };
}
//# sourceMappingURL=fulfillment-status.js.map